import { useState } from 'react';
import api from '../services/api';

/**
 * @module useDeleteQuestionnaire
 * @description Custom hook for deleting questionnaires in the Mental Health Portal application.
 * Handles API communication, loading states, and error handling.
 *
 * @requires react
 * @requires ../services/api
 */
export function useDeleteQuestionnaire() {
  /**
   * @state {boolean} loading - Indicates if the API request is in progress
   */
  const [loading, setLoading] = useState(false);

  /**
   * @state {string|null} error - Error message if the API request fails, null otherwise
   */
  const [error, setError] = useState(null);

  /**
   * Deletes a questionnaire by ID
   * @async
   * @function deleteQuestionnaire
   * @param {string|number} id - The ID of the questionnaire to delete
   * @returns {Promise<void>}
   * @throws {Error} If the API request fails
   */
  const deleteQuestionnaire = async (id) => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.delete(`questionnaire/${id}`);

      if (!response.ok) {
        throw new Error(`Erro ao deletar: ${response.statusText}`);
      }
    } catch (err) {
      setError(err.message || 'Erro ao deletar questionário.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * @typedef {Object} UseDeleteQuestionnaireReturn
   * @property {Function} deleteQuestionnaire - Function to delete a questionnaire
   * @property {boolean} loading - Indicates if the API request is in progress
   * @property {string|null} error - Error message if the API request fails, null otherwise
   */

  /**
   * @returns {UseDeleteQuestionnaireReturn} The hook's return values
   */
  return { deleteQuestionnaire, loading, error };
}
